import { purchaseTivigoPackage } from '../creditCheckoutApi';
import { resolveAdminCreditAdjustment } from '../utils/adminCreditAdjustment';
import {
  isCredentialValueAllowed,
  PASSWORD_CHARACTER_ERROR,
} from '../utils/credentialPolicy';

const USER_ENDPOINT_URL = 'https://tivigo.store/endpoint.php';
const ADMIN_ENDPOINT_URL = 'https://tivigo.store/service-endpoint.php';
const MAIL_ENDPOINT_URL = 'https://tivigo.store/mail-endpoint.php';
const APP_ENDPOINT_URL = 'https://tivigo.store/app-endpoint.php';
const PLAYER_API_URL = 'https://tivigo.store/player_api.php';
const STREAM_BASE_URL = 'https://streams.tivigoiptv.info';

const readJsonStorage = (key) => {
  if (typeof window === 'undefined') return {};
  try {
    return JSON.parse(window.localStorage.getItem(key) || '{}');
  } catch (error) {
    return {};
  }
};

export const getStoredToken = () => (
  typeof window === 'undefined' ? '' : window.localStorage.getItem('xtream_api_token') || ''
);

export const getStoredCredentials = () => readJsonStorage('xtream_credentials');

export const getStoredXtreamSession = () => {
  const credentials = getStoredCredentials();
  const user = readJsonStorage('xtream_user');
  const server = user?.server_info?.url || credentials.server || 'https://tivigo.store';
  const username = String(credentials.username || user?.user_info?.username || '').trim();
  const password = String(credentials.password || user?.user_info?.password || '');

  if (!username || !password) {
    throw new Error('Kullanıcı yayın bilgileri bulunamadı. Lütfen yeniden giriş yapın.');
  }

  return { server, username, password };
};

const appendParams = (url, params = {}) => {
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    if (Array.isArray(value)) {
      value.forEach((item) => url.searchParams.append(key, String(item)));
      return;
    }
    url.searchParams.set(key, String(value));
  });
};

const readPayload = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    throw new Error('Sunucu yanıtı okunamadı.');
  }
};

const request = async (baseUrl, endpoint, params = {}, options = {}) => {
  const url = new URL(baseUrl);
  url.searchParams.set('endpoint', endpoint);

  if (options.auth !== false) {
    const token = options.token || getStoredToken();
    if (!token) throw new Error('Oturum anahtarı bulunamadı. Lütfen yeniden giriş yapın.');
    url.searchParams.set('token', token);
  }

  appendParams(url, params);

  const fetchOptions = {
    method: options.method || 'GET',
    cache: 'no-store',
    ...(options.fetchOptions || {}),
  };

  if (options.body !== undefined) {
    fetchOptions.headers = {
      'Content-Type': 'application/json',
      ...(fetchOptions.headers || {}),
    };
    fetchOptions.body = JSON.stringify(options.body);
  }

  const response = await fetch(url.toString(), fetchOptions);
  const payload = await readPayload(response);

  if (!response.ok || payload?.success === false) {
    throw new Error(payload?.message || payload?.data || `Sunucu isteği başarısız (${response.status}).`);
  }

  return payload;
};

export const endpointRequest = (endpoint, params, options) => (
  request(USER_ENDPOINT_URL, endpoint, params, options)
);

export const serviceEndpointRequest = (endpoint, params, options) => (
  request(ADMIN_ENDPOINT_URL, endpoint, params, options)
);

export const mailEndpointRequest = (endpoint, params, options) => (
  request(MAIL_ENDPOINT_URL, endpoint, params, options)
);

export const appEndpointRequest = (endpoint, params, options) => (
  request(APP_ENDPOINT_URL, endpoint, params, options)
);

const resolveTargetUserId = (payload) => (
  payload?.targetUserId
  || payload?._targetUserId
  || payload?.userInfo?._targetUserId
  || payload?.userInfo?.target_user_id
  || null
);

const targetParams = (targetUserId) => (targetUserId ? { target_user_id: targetUserId } : {});

const accountChangeRequest = async (endpoint, body, targetUserId) => {
  const token = getStoredToken();
  if (!token) throw new Error('Oturum anahtarı bulunamadı. Lütfen yeniden giriş yapın.');
  const result = await endpointRequest(endpoint, targetParams(targetUserId), {
    auth: false,
    method: 'POST',
    body: { token, ...body },
  });
  const nextToken = result?.data?.token;
  if (!targetUserId && typeof nextToken === 'string' && nextToken && typeof window !== 'undefined') {
    try {
      window.localStorage.setItem('xtream_api_token', nextToken);
    } catch (error) {
      // Yeni token saklanamazsa sonraki istekte yeniden giriş istenir.
    }
  }
  return result;
};

export const userApi = {
  async getUserData(targetUserId = null) {
    return endpointRequest('user_data', targetParams(targetUserId));
  },

  async getNotificationStats() {
    return endpointRequest('notification_stats');
  },

  async getTicketStats() {
    return endpointRequest('ticket_stats');
  },

  async getNotifications({ page = 1, limit = 20 } = {}) {
    return endpointRequest('notifications', { action: 'list', page, limit });
  },

  async markNotificationRead(id) {
    return endpointRequest('notifications', { action: 'mark_read', id });
  },

  async deleteNotification(id) {
    return endpointRequest('notifications', { action: 'delete', id });
  },

  async getResellerUsers({ page = 1, limit = 1000, search = '' } = {}) {
    return endpointRequest('get_reseller_users', { page, limit, search });
  },

  async getResellerStats() {
    return endpointRequest('get_reseller_stats');
  },

  async getResellerInfo() {
    return endpointRequest('get_reseller_info');
  },

  async getResellerTestLimit() {
    return endpointRequest('get_reseller_test_limit');
  },

  async createResellerUser(payload) {
    return endpointRequest('reseller_add_user', {
      username: payload.username,
      password: payload.password,
      email: payload.email,
      phone: payload.phone,
      is_test: payload.is_test ? 1 : 0,
      ad: payload.ad || '',
      takim: payload.takim || '',
      max_connections: payload.max_connections || 1,
    });
  },

  async removeResellerUser(userId) {
    return endpointRequest('remove_user_from_reseller', { user_id: userId });
  },

  async loadAccount(payload = {}) {
    return endpointRequest('user_data', targetParams(resolveTargetUserId(payload)));
  },

  async updateProfile(payload) {
    return endpointRequest('update_profile', {
      ad: payload.name,
      takim: payload.team || '',
      ...targetParams(resolveTargetUserId(payload)),
    });
  },

  // Şifre ve iletişim değişiklikleri POST gövdesiyle gider (şifre adres satırına ve loglara düşmez).
  // Sunucu mevcut e-postayı / telefonu kendisi doğrular ve kendi hesabında eski oturumları kapatıp yeni token döner.
  async changePassword(payload) {
    return accountChangeRequest('change_password', {
      current_password: payload.currentPassword,
      new_password: payload.newPassword,
      current_email: payload.email,
    }, resolveTargetUserId(payload));
  },

  // E-posta degisikligi icin mevcut (kayitli) adrese 6 haneli dogrulama kodu gonderir.
  async sendEmailChangeCode(payload) {
    return accountChangeRequest('send_email_change_code', {
      new_email: payload.newEmail,
    }, resolveTargetUserId(payload));
  },

  // Kodu dogrular ve adresi degistirir. update_contact kullanilmaz; e-postanin kendi ucu var.
  async updateEmail(payload) {
    return accountChangeRequest('verify_email_change', {
      new_email: payload.newEmail,
      code: payload.code,
    }, resolveTargetUserId(payload));
  },

  // Telefon degisikligi de OTP ile: kod hesaba kayitli e-posta adresine gider.
  async sendPhoneChangeCode(payload) {
    return accountChangeRequest('send_phone_change_code', {
      new_phone: payload.newPhone,
    }, resolveTargetUserId(payload));
  },

  async updatePhone(payload) {
    return accountChangeRequest('verify_phone_change', {
      new_phone: payload.newPhone,
      code: payload.code,
    }, resolveTargetUserId(payload));
  },

  async listTrustedDevices() {
    return endpointRequest('list_trusted_devices');
  },

  // Oturum kayitlari: kullanici yalniz kendi giris gecmisini gorur.
  async listLoginHistory({ limit = 20 } = {}) {
    return endpointRequest('login_history', { limit });
  },

  // "Bu ben degilim": tum oturumlari kapatir, kaydi CMS incelemesine dusurur.
  async reportUnauthorizedLogin({ loginId }) {
    return endpointRequest('report_unauthorized_login', {}, {
      method: 'POST',
      body: { login_id: loginId },
    });
  },

  async removeTrustedDevice(payload) {
    const deviceId = payload?.deviceId ?? payload?.id ?? payload;
    return endpointRequest('remove_trusted_device', { device_id: deviceId });
  },

  async listBouquets({ targetUserId = null } = {}) {
    return endpointRequest('list_bouquets', targetParams(targetUserId));
  },

  async updateBouquets({ targetUserId = null, toAdd = [], toRemove = [] } = {}) {
    const results = [];
    for (const bouquetId of toAdd) {
      results.push(await endpointRequest('manage_bouquets', {
        action: 'add',
        bouquet_id: bouquetId,
        ...targetParams(targetUserId),
      }));
    }
    for (const bouquetId of toRemove) {
      results.push(await endpointRequest('manage_bouquets', {
        action: 'remove',
        bouquet_id: bouquetId,
        ...targetParams(targetUserId),
      }));
    }
    return { success: true, results };
  },

  async getSecurityLogs({ userInfo, targetUserId = null, filter = 'success', page = 1, limit = 20 } = {}) {
    const resolvedTargetUserId = resolveTargetUserId({ userInfo, targetUserId });
    const payload = await endpointRequest('security_logs', {
      filter: 'all',
      limit: 1000,
      page: 1,
      ...targetParams(resolvedTargetUserId),
    });
    let logs = Array.isArray(payload?.data?.logs) ? [...payload.data.logs] : [];
    logs.sort((first, second) => Number(second.id) - Number(first.id));
    logs = logs.filter((log) => log.attempt_step !== 'username_check');
    logs = logs.filter((log) => !(
      (log.attempt_step === 'email_verify' || log.attempt_step === 'phone_verify')
      && Number(log.success) === 1
    ));
    if (filter === 'success') {
      logs = logs.filter((log) => Number(log.success) === 1 && log.event_type === 'login_attempt');
    } else if (filter === 'failed') {
      logs = logs.filter((log) => Number(log.success) === 0 && log.event_type === 'login_attempt');
    } else if (filter === 'password_reset') {
      logs = logs.filter((log) => (
        log.event_type === 'password_reset'
        || ['email_verify', 'phone_verify', 'password_reset', 'rate_limit_blocked'].includes(log.attempt_step)
      ));
    }
    const total = logs.length;
    const pages = Math.max(1, Math.ceil(total / limit));
    const safePage = Math.min(Math.max(1, Number(page) || 1), pages);
    const start = (safePage - 1) * limit;
    return {
      logs: logs.slice(start, start + limit),
      pagination: { page: safePage, pages, total },
    };
  },

  async getTransactionHistory() {
    const payload = await endpointRequest('kredi_gecmisi', { limit: 1000, page: 1 });
    return { history: payload?.data?.history || [] };
  },

  async getReferrals({ page = 1, limit = 1000 } = {}) {
    return endpointRequest('referrals', { page, limit });
  },

  async claimReferralBonus(referredUserId) {
    return endpointRequest('claim_referral_bonus', { referred_user_id: referredUserId });
  },

  async getTickets({ page = 1, limit = 1000, status = '' } = {}) {
    return endpointRequest('tickets', { page, limit, status });
  },

  async getTicketDetail(ticketId) {
    return endpointRequest('ticket_detail', { ticket_id: ticketId });
  },

  async createTicket(payload) {
    return endpointRequest('ticket_create', {
      category: payload.category,
      priority: payload.priority,
      title: payload.title,
      message: payload.message,
    });
  },

  async replyTicket({ ticketId, message }) {
    return endpointRequest('ticket_reply', { ticket_id: ticketId, message });
  },

  async editTicketReply({ replyId, message }) {
    return endpointRequest('edit_ticket_reply', { reply_id: replyId, message });
  },

  async setTicketStatus({ ticketId, status }) {
    return endpointRequest('ticket_close', { ticket_id: ticketId, status });
  },

  async validateCoupon({ code }) {
    const payload = await endpointRequest('validate_kupon', { kupon_kodu: code });
    return payload?.data || null;
  },

  async purchaseDuration({ duration, maxConnections, coupon, userInfo, pricing }) {
    const targetUserId = resolveTargetUserId({ userInfo });
    if (!targetUserId) {
      return purchaseTivigoPackage({
        userInfo,
        duration,
        maxConnections,
        couponCode: coupon?.code || coupon?.kod || '',
        ...(pricing ? { pricing } : {}),
      });
    }

    if (coupon?.code || coupon?.kod) {
      const couponPayload = await endpointRequest('use_kupon', {
        kupon_kodu: coupon.code || coupon.kod,
        paket_2k: Number(maxConnections) > 1 ? 1 : 0,
      });
      if (couponPayload?.success === false) throw new Error(couponPayload.message || 'Kupon kullanılamadı.');
    }

    const payload = await endpointRequest('extend_user_subscription_as_reseller', {
      user_id: targetUserId,
      duration,
      max_connections: Number(maxConnections) === 2 ? 2 : 1,
      // The server rejects the purchase if the price list changed after this screen loaded it.
      ...(pricing ? { pricing_revision: pricing.revision, pricing_currency: pricing.currency } : {}),
    });

    return payload?.data || payload;
  },

  async getComments({ pageType, pageRef = '' }) {
    const payload = await endpointRequest('get_comments', {
      sayfa_tipi: pageType,
      sayfa_ref: pageRef,
      token: getStoredToken(),
    }, { auth: false });
    return payload?.data?.yorumlar || [];
  },

  async addComment({ pageType, pageRef = '', comment, parentId = '', rating = '', liked = '' }) {
    const supportsRating = pageType === 'film' || pageType === 'dizi';
    return endpointRequest('add_comment', {
      sayfa_tipi: pageType,
      sayfa_ref: pageRef,
      yorum: comment,
      parent_id: parentId,
      rating: supportsRating ? rating : '',
      begenildi: liked,
    });
  },

  async toggleCommentHelpful(commentId) {
    return endpointRequest('toggle_helpful', { yorum_id: commentId });
  },
};

const contentParams = () => {
  const session = getStoredXtreamSession();
  return {
    server: session.server,
    username: session.username,
    password: session.password,
  };
};

export const contentApi = {
  async getMovies() {
    const session = contentParams();
    const [categoriesPayload, moviesPayload, recentlyAddedPayload, hiddenPayload] = await Promise.all([
      endpointRequest('get_vod_categories', session),
      endpointRequest('get_vod_streams', session),
      endpointRequest('get_recently_added_vods', { ...session, days: 30 }).catch(() => ({ data: [] })),
      endpointRequest('get_hidden_contents').catch(() => ({ data: {} })),
    ]);
    const categories = Array.isArray(categoriesPayload?.data) ? categoriesPayload.data : [];
    const categoryMap = new Map(categories.map((item) => [String(item.category_id), item.category_name]));
    const categoryOrderMap = new Map(
      categories.map((item, index) => [String(item.category_id), index])
    );
    const recentlyAdded = Array.isArray(recentlyAddedPayload?.data)
      ? recentlyAddedPayload.data
      : Array.isArray(recentlyAddedPayload?.data?.newlyAdded)
        ? recentlyAddedPayload.data.newlyAdded
        : [];
    const recentlyAddedRank = new Map();
    recentlyAdded.forEach((item, index) => {
      const movieId = String(item?.stream_id || item?.id || '');
      if (movieId && !recentlyAddedRank.has(movieId)) recentlyAddedRank.set(movieId, index);
    });
    const hiddenIds = new Set((hiddenPayload?.data?.filmler || hiddenPayload?.data?.vod || []).map(String));
    const primaryMovies = Array.isArray(moviesPayload?.data) ? moviesPayload.data : [];
    const mergedMovies = [...primaryMovies];
    const mergedMovieIds = new Set(primaryMovies.map((item) => String(item?.stream_id || item?.id || '')));
    recentlyAdded.forEach((item) => {
      const movieId = String(item?.stream_id || item?.id || '');
      if (!movieId || mergedMovieIds.has(movieId)) return;
      mergedMovieIds.add(movieId);
      mergedMovies.push(item);
    });
    const movies = mergedMovies
      .filter((item) => item.stream_icon && !hiddenIds.has(String(item.stream_id)))
      .map((item) => ({
        ...item,
        id: item.stream_id,
        poster: item.stream_icon,
        categoryName: categoryMap.get(String(item.category_id)) || item.category_name || 'Diğer Filmler',
        categoryKnown: categoryMap.has(String(item.category_id)),
        categoryOrder: categoryOrderMap.get(String(item.category_id)) ?? Number.MAX_SAFE_INTEGER,
        recentlyAddedRank: recentlyAddedRank.has(String(item.stream_id))
          ? recentlyAddedRank.get(String(item.stream_id))
          : null,
      }));
    return { categories, movies };
  },

  async getMovieDetail(movie) {
    const payload = await endpointRequest('get_vod_info', {
      ...contentParams(),
      vod_id: movie?.stream_id || movie?.id,
    });
    const detail = payload?.data || {};
    const info = detail.info || detail;
    return {
      ...movie,
      ...info,
      detail,
      poster: info.movie_image || movie?.poster || movie?.stream_icon,
      backdrop: info.backdrop_path?.[0] || info.movie_image || movie?.backdrop,
      overview: info.plot || info.description || movie?.overview,
      description: info.plot || info.description || movie?.description,
    };
  },

  async getSeries() {
    const session = contentParams();
    const [categoriesPayload, seriesPayload, recentlyAddedPayload, hiddenPayload] = await Promise.all([
      endpointRequest('get_series_categories', session),
      endpointRequest('get_series', session),
      endpointRequest('get_recently_added_series', session).catch(() => ({ data: {} })),
      endpointRequest('get_hidden_contents').catch(() => ({ data: {} })),
    ]);
    const categories = Array.isArray(categoriesPayload?.data) ? categoriesPayload.data : [];
    const categoryMap = new Map(categories.map((item) => [String(item.category_id), item.category_name]));
    const recentlyAdded = Array.isArray(recentlyAddedPayload?.data?.newlyAdded)
      ? recentlyAddedPayload.data.newlyAdded
      : [];
    const recentlyAddedRank = new Map();
    recentlyAdded.forEach((item, index) => {
      const seriesId = String(item?.series_id || item?.id || '');
      if (seriesId && !recentlyAddedRank.has(seriesId)) recentlyAddedRank.set(seriesId, index);
    });
    const hiddenIds = new Set((hiddenPayload?.data?.diziler || hiddenPayload?.data?.series || []).map(String));
    const series = (Array.isArray(seriesPayload?.data) ? seriesPayload.data : [])
      .filter((item) => item.cover && !hiddenIds.has(String(item.series_id)))
      .map((item) => ({
        ...item,
        id: item.series_id,
        poster: item.cover,
        platformId: String(item.category_id || 'tivigo'),
        platformName: categoryMap.get(String(item.category_id)) || item.category_name || 'Tivigo',
        recentlyAddedRank: recentlyAddedRank.has(String(item.series_id))
          ? recentlyAddedRank.get(String(item.series_id))
          : null,
      }));
    return { categories, series };
  },

  async getSeriesDetail(series) {
    const payload = await endpointRequest('get_series_info', {
      ...contentParams(),
      series_id: series?.series_id || series?.id,
    });
    const detail = payload?.data || {};
    const info = detail.info || detail;
    return {
      ...series,
      ...info,
      detail,
      poster: info.cover || series?.poster || series?.cover,
      backdrop: info.backdrop_path?.[0] || info.cover || series?.backdrop,
      overview: info.plot || info.description || series?.overview,
      description: info.plot || info.description || series?.description,
    };
  },

  async getLiveTv() {
    const { username, password } = getStoredXtreamSession();
    const createUrl = (action) => {
      const url = new URL(PLAYER_API_URL);
      appendParams(url, { username, password, action });
      return url;
    };
    const [categoryResponse, channelResponse, hiddenPayload] = await Promise.all([
      fetch(createUrl('get_live_categories'), { cache: 'no-store' }),
      fetch(createUrl('get_live_streams'), { cache: 'no-store' }),
      endpointRequest('get_hidden_contents').catch(() => ({ data: {} })),
    ]);
    const [categories, channels] = await Promise.all([readPayload(categoryResponse), readPayload(channelResponse)]);
    if (!categoryResponse.ok || !channelResponse.ok || !Array.isArray(categories) || !Array.isArray(channels)) {
      throw new Error('Canlı TV kanal listesi alınamadı.');
    }
    const categoryMap = new Map(categories.map((item) => [String(item.category_id), item.category_name]));
    const hiddenIds = new Set((hiddenPayload?.data?.webtv || []).map(String));
    return {
      categories,
      channels: channels
        .filter((item) => !hiddenIds.has(String(item.stream_id)))
        .map((item) => ({
          ...item,
          id: String(item.stream_id),
          logo: item.stream_icon,
          categoryId: String(item.category_id),
          categoryName: categoryMap.get(String(item.category_id)) || 'Canlı TV',
        })),
    };
  },

  buildLiveStreamUrl(channel) {
    const { username, password } = getStoredXtreamSession();
    return `${STREAM_BASE_URL}/live/${encodeURIComponent(username)}/${encodeURIComponent(password)}/${channel.stream_id || channel.id}.m3u8`;
  },

  buildMovieStreamUrl(movie) {
    const { username, password } = getStoredXtreamSession();
    const extension = movie?.container_extension || movie?.detail?.movie_data?.container_extension || 'mp4';
    return `${STREAM_BASE_URL}/movie/${encodeURIComponent(username)}/${encodeURIComponent(password)}/${movie.stream_id || movie.id}.${extension}`;
  },

  buildSeriesStreamUrl(episode) {
    const { username, password } = getStoredXtreamSession();
    const extension = episode?.container_extension || 'mkv';
    return `${STREAM_BASE_URL}/series/${encodeURIComponent(username)}/${encodeURIComponent(password)}/${episode.id}.${extension}`;
  },
};

const numericValue = (value) => {
  let normalized = String(value ?? '').trim().replace(/[^0-9,.-]/g, '');
  if (normalized.includes(',') && normalized.includes('.')) {
    normalized = normalized.lastIndexOf(',') > normalized.lastIndexOf('.')
      ? normalized.replace(/\./g, '').replace(',', '.')
      : normalized.replace(/,/g, '');
  } else if (normalized.includes(',')) {
    normalized = /^-?\d{1,3}(?:,\d{3})+$/.test(normalized) ? normalized.replace(/,/g, '') : normalized.replace(',', '.');
  } else if (/^-?\d{1,3}(?:\.\d{3})+$/.test(normalized)) {
    normalized = normalized.replace(/\./g, '');
  }
  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeQuickCredential = (value) => String(value || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');
const generateQuickCredential = () => `x${Math.floor(Math.random() * 100000).toString().padStart(5, '0')}`;

// Yeni test hesaplarinin suresi (saat). Kaynak: sistem_ayarlari > test_suresi_saat.
// Sunucuya ulasilamazsa veya deger beklenmedikse 3 saate duser (endpoint.php ile ayni davranis).
const fetchTrialHours = async () => {
  try {
    const result = await endpointRequest('check_test_yayini_durumu', {}, { auth: false });
    const hours = Number(result?.data?.test_suresi_saat);
    return [1, 2, 3].includes(hours) ? hours : 3;
  } catch (error) {
    return 3;
  }
};

export const adminApi = {
  request: serviceEndpointRequest,

  getDashboardStats() {
    return serviceEndpointRequest('dashboard_stats');
  },

  getTodayLoginUsers({ page = 1, limit = 1000 } = {}) {
    return serviceEndpointRequest('today_login_users', { page, limit });
  },

  getDashboardChart(range = 'week') {
    return serviceEndpointRequest('chart_data', { range });
  },

  getUsers({ page = 1, limit = 1000, filter = '', search = '' } = {}) {
    return serviceEndpointRequest('all_users', { page, limit, filter, search });
  },

  getUsersForSelect() {
    return serviceEndpointRequest('get_users_for_select');
  },

  getUserDetail(userId) {
    return serviceEndpointRequest('user_detail', { user_id: userId });
  },

  getAccountSecurityHistory(userId, filters = {}, fetchOptions = {}) {
    return serviceEndpointRequest('get_account_security_history', { ...filters, user_id: userId }, { fetchOptions });
  },

  getUserResellerInfo(userId) {
    return endpointRequest('get_user_reseller_info', { user_id: userId });
  },

  async createUser(values) {
    const registration = await endpointRequest('register_with_google', {
      username: values.username,
      password: values.password,
      email: values.email,
      phone: values.phone,
      ad: values.fullName || '',
      type: 'normal',
      registration_type: 'admin_created',
    }, { auth: false });
    const userId = registration?.data?.user?.id;
    if (!userId) throw new Error('Yeni kullanıcı kimliği alınamadı.');
    const update = { user_id: userId };
    if (values.fullName) update.ad = values.fullName;
    if (values.team) update.takim = values.team;
    if (numericValue(values.credit) > 0) update.kredi = numericValue(values.credit);
    if (values.parentDealer) update.member_id = values.parentDealer;
    if (numericValue(values.subDealer) > 0) update.sahibi = numericValue(values.subDealer);
    if (Number(values.maxConnections) !== 1) update.max_connections = values.maxConnections;
    if (numericValue(values.expDate) > 0) update.exp_date = values.expDate;
    if (!values.enabled) update.enabled = 0;
    if (values.banned) update.admin_enabled = 0;
    if (Object.keys(update).length > 1) await serviceEndpointRequest('edit_user', update);
    return { ...registration, userId };
  },

  async updateUser(userId, values, original = {}) {
    if (Object.prototype.hasOwnProperty.call(values, 'creditAdjustmentDirection')) {
      const adjustment = resolveAdminCreditAdjustment({
        amount: values.creditAdjustment, direction: values.creditAdjustmentDirection,
        currency: values.creditAdjustmentCurrency, originalCredit: numericValue(original.kredi),
        eurEnabled: true, eurTryRate: values.creditAdjustmentRate,
      });
      if (adjustment.hasAmount && !adjustment.valid) throw new Error(adjustment.error);
      values = { ...values, credit: adjustment.nextCredit, creditAdjustmentNativeAmount: adjustment.nativeAmount };
    }
    const update = { user_id: userId };
    if (String(values.fullName || '') !== String(original.ad || '')) update.ad = values.fullName || '';
    if (String(values.team || '') !== String(original.takim || '')) update.takim = values.team || '';
    if (numericValue(values.credit) !== numericValue(original.kredi)) {
      update.kredi = numericValue(values.credit);
      const nativeAmount = numericValue(values.creditAdjustmentNativeAmount);
      const adjustmentRate = Number(values.creditAdjustmentRate);
      if (values.creditAdjustmentCurrency === 'EUR' && nativeAmount !== 0 && Number.isFinite(adjustmentRate) && adjustmentRate > 0) {
        update.credit_adjustment_currency = 'EUR';
        update.credit_adjustment_native_amount = nativeAmount;
        update.credit_adjustment_rate = adjustmentRate;
      }
    }
    if (String(values.parentDealer || '') !== String(original.member_id || '')) update.member_id = values.parentDealer || '';
    if (numericValue(values.subDealer) !== numericValue(original.sahibi)) update.sahibi = numericValue(values.subDealer);
    if (String(values.paymentMethod || 'otomatik') !== String(original.kredi_modal || 'otomatik')) update.kredi_modal = values.paymentMethod || 'otomatik';
    if (String(values.username || '') !== String(original.username || '')) update.username = values.username;
    if (String(values.email || '') !== String(original.email || '')) update.email = values.email;
    if (String(values.phone || '') !== String(original.phone || '')) update.phone = values.phone;
    if (Number(values.maxConnections || 1) !== Number(original.max_connections || 1)) update.max_connections = values.maxConnections || 1;
    if ((values.enabled ? 1 : 0) !== Number(original.enabled)) update.enabled = values.enabled ? 1 : 0;
    if ((values.banned ? 0 : 1) !== Number(original.admin_enabled)) update.admin_enabled = values.banned ? 0 : 1;
    if (values.duration !== 'Değiştirme' && values.expDate !== undefined) update.exp_date = values.expDate;
    let result = null;
    if (Object.keys(update).length > 1) result = await serviceEndpointRequest('edit_user', update);
    if (values.password && values.password !== original.password) {
      if (!isCredentialValueAllowed(values.password)) {
        throw new Error(PASSWORD_CHARACTER_ERROR);
      }
      await serviceEndpointRequest('admin_change_user_password', {
        user_id: userId,
        new_password: values.password,
      });
    }
    const originalPackage = original.reseller_package || original.reseller_level || '';
    if (values.resellerPackage !== originalPackage) {
      if (!values.resellerPackage) throw new Error('Bayi paketi kaldırma işlemi mevcut serviste desteklenmiyor.');
      await endpointRequest('assign_reseller_package_admin', {
        user_id: userId,
        package: values.resellerPackage,
      });
    }
    return result || { success: true, message: 'Değişiklik bulunamadı.' };
  },

  async createQuickTest(values) {
    let username = normalizeQuickCredential(values.username);
    let password = normalizeQuickCredential(values.password);
    if (username.length < 4) username = generateQuickCredential();
    if (password.length < 6) password = generateQuickCredential();
    const registration = await endpointRequest('register_quick_test', {
      username,
      password,
      email: values.email || '',
      phone: values.phone || '',
      ad: '',
      type: 'quick_test',
      registration_type: 'admin_created',
    }); // TIVIGO-TRIALFIX 2026-10-09: sunucu artık admin JWT ister (register_quick_test kimliksizdi, bot kullandı)
    const userId = registration?.data?.user?.id;
    if (!userId) throw new Error('Yeni test kullanıcı kimliği alınamadı.');
    // Test suresi sistem ayarindan gelir (sistem_ayarlari > test_suresi_saat). Burada sabit 3 saat
    // yazmak, ayarda ne secilirse secilsin hesabi 3 saate cekiyordu.
    const trialHours = await fetchTrialHours();
    await serviceEndpointRequest('edit_user', {
      user_id: userId,
      exp_date: Math.floor(Date.now() / 1000) + (trialHours * 3600),
      enabled: 1,
      admin_enabled: 1,
      max_connections: 1,
      email: values.email || '',
      phone: values.phone || '',
    });
    const quickLogin = await endpointRequest('generate_quick_login', { user_id: userId });
    return { registration, userId, username, password, code: quickLogin?.data?.code || '' };
  },

  generateQuickLogin(userId) {
    return endpointRequest('generate_quick_login', { user_id: userId });
  },

  getTickets({ page = 1, limit = 1000, status = '', search = '' } = {}) {
    return serviceEndpointRequest('all_tickets', { page, limit, status, search });
  },

  getTicketStats() {
    return serviceEndpointRequest('admin_ticket_stats');
  },

  getTicketDetail(ticketId) {
    return serviceEndpointRequest('admin_ticket_detail', { ticket_id: ticketId });
  },

  replyTicket(ticketId, message) {
    return serviceEndpointRequest('admin_ticket_reply', { ticket_id: ticketId, message });
  },

  editTicketReply(replyId, message) {
    return serviceEndpointRequest('admin_edit_ticket_reply', { reply_id: replyId, message });
  },

  setTicketStatus(ticketId, status) {
    return serviceEndpointRequest('admin_ticket_status', { ticket_id: ticketId, status });
  },

  getComments({ page = 1, limit = 1000, filter = '' } = {}) {
    return serviceEndpointRequest('all_comments', { page, limit, filter });
  },

  moderateComment(commentId, status) {
    return serviceEndpointRequest('moderate_comment', { yorum_id: commentId, durum: status });
  },

  deleteComment(commentId) {
    return serviceEndpointRequest('delete_comment', { yorum_id: commentId });
  },

  getReport(type, params = {}) {
    const endpoints = {
      security: 'admin_security_logs',
      credit: 'admin_credit_history',
      referral: 'admin_referral_stats',
      email: 'admin_email_logs',
      content: 'admin_content_errors',
    };
    const endpoint = endpoints[type];
    if (!endpoint) throw new Error('Bilinmeyen rapor türü.');
    return serviceEndpointRequest(endpoint, { page: 1, limit: 1000, ...params });
  },

  getReferralDetail(userId) {
    return serviceEndpointRequest('admin_referral_detail', { user_id: userId });
  },

  clearReport(type) {
    const endpoints = {
      security: 'clear_security_logs',
      credit: 'clear_credit_history',
      email: 'clear_email_logs',
      content: 'clear_content_errors',
    };
    const endpoint = endpoints[type];
    if (!endpoint) throw new Error('Bu rapor türü toplu olarak temizlenemez.');
    return serviceEndpointRequest(endpoint);
  },

  deleteContentError(contentType, contentId) {
    return serviceEndpointRequest('delete_content_error', {
      content_type: contentType,
      content_id: contentId,
    });
  },

  getQuickReplies(search = '') {
    return serviceEndpointRequest('get_hizli_cevaplar', { search });
  },

  saveQuickReply(values, id = null) {
    return serviceEndpointRequest(id ? 'edit_hizli_cevap' : 'add_hizli_cevap', {
      id,
      baslik: values.templateTitle,
      mesaj: values.message,
      kategori: values.category,
      keywords: values.keywords,
    });
  },

  deleteQuickReply(id) {
    return serviceEndpointRequest('delete_hizli_cevap', { id });
  },

  getMacros() {
    return serviceEndpointRequest('get_macros');
  },

  saveMacro(values, id = null) {
    return serviceEndpointRequest(id ? 'update_macro' : 'create_macro', {
      id,
      macro_key: values.shortcut,
      label: values.templateTitle,
      text: values.message,
    });
  },

  deleteMacro(id) {
    return serviceEndpointRequest('delete_macro', { id });
  },

  getSystemSettings() {
    return serviceEndpointRequest('get_sistem_ayarlari');
  },

  getPackagePricingSettings() {
    return serviceEndpointRequest('get_package_pricing_settings');
  },

  savePackagePricingSettings(settings) {
    return serviceEndpointRequest('save_package_pricing_settings', {}, { method: 'POST', body: settings });
  },

  updateSystemSetting(key, value) {
    return serviceEndpointRequest('update_sistem_ayar', { key, value });
  },

  getBouquets() {
    return serviceEndpointRequest('get_bouquets');
  },

  getTestRecords() {
    return serviceEndpointRequest('get_test_kayitlari');
  },

  deleteTestRecord(id) {
    return serviceEndpointRequest('delete_test_kaydi', { id });
  },

  clearTestRecords() {
    return serviceEndpointRequest('clear_test_kayitlari');
  },

  getCoupons({ page = 1, limit = 1000, filter = 'all' } = {}) {
    return serviceEndpointRequest('list_kuponlar', { page, limit, filter });
  },

  createCoupons(values) {
    return serviceEndpointRequest('create_kupon', {
      tutar: values.amount,
      adet: values.quantity,
      gecerlilik_gun: values.validity,
      admin_notu: values.note || '',
      sadece_2k: values.onlyTwo ? 1 : 0,
    });
  },

  deleteCoupon(id) {
    return serviceEndpointRequest('delete_kupon', { kupon_id: id });
  },

  toggleCoupon(id, active) {
    return serviceEndpointRequest('toggle_kupon', { kupon_id: id, aktif: active ? 1 : 0 });
  },

  getActionHistory({ page = 1, limit = 5, search = '', dateFilter = 'all', actionType = 'all', status = 'all' } = {}) {
    return serviceEndpointRequest('get_action_history', {
      page,
      limit,
      search,
      date_filter: dateFilter,
      action_type: actionType,
      status,
    });
  },

  getActionHistoryDetail(historyId, { page = 1, limit = 20, search = '', status = 'all' } = {}) {
    return serviceEndpointRequest('get_action_history_detail', {
      history_id: historyId,
      page,
      limit,
      search,
      status,
    });
  },

  getBulkMailJobStatus(jobId) {
    return serviceEndpointRequest('get_bulk_mail_job_status', { job_id: jobId });
  },

  getAutoTasks() {
    return serviceEndpointRequest('get_auto_tasks');
  },

  createAutoTask(task) {
    return serviceEndpointRequest('create_auto_task', {
      name: task.name,
      description: task.description,
      task_endpoint: task.endpoint,
      enabled: task.enabled ? 1 : 0,
    });
  },

  updateAutoTask(id, enabled) {
    return serviceEndpointRequest('update_auto_task', { task_id: id, enabled: enabled ? 1 : 0 });
  },

  deleteAutoTask(id) {
    return serviceEndpointRequest('delete_auto_task', { task_id: id });
  },

  runAutoTask(id) {
    return serviceEndpointRequest('execute_single_task', { task_id: id });
  },

  runAutoTasks() {
    return serviceEndpointRequest('execute_auto_tasks');
  },

  getOldTicketsCount() {
    return serviceEndpointRequest('get_old_tickets_count');
  },

  closeOldTickets() {
    return serviceEndpointRequest('auto_close_old_tickets');
  },

  bulkPreview(userGroup, userId = '') {
    return serviceEndpointRequest('bulk_preview', { user_group: userGroup, user_id: userId });
  },

  searchUsers(search) {
    return serviceEndpointRequest('all_users', { search, limit: 10, page: 1 });
  },

  sendBulkNotification({ userGroup, title, content, sendEmail, userId = '' }) {
    return serviceEndpointRequest('bulk_send_notification', {
      user_group: userGroup,
      baslik: title,
      icerik: content,
      send_email: sendEmail ? 1 : 0,
      user_id: userId,
    });
  },

  bulkAddTime({ userGroup, days, userId = '' }) {
    return serviceEndpointRequest('bulk_add_time', {
      user_group: userGroup,
      days,
      send_notification: 1,
      user_id: userId,
    });
  },

  getMailCampaignSummary() {
    return mailEndpointRequest('mail_campaign_summary');
  },

  getMailLists({ page = 1, limit = 20, search = '', status = 'all' } = {}) {
    return mailEndpointRequest('mail_lists', { page, limit, search, status });
  },

  createMailList({ name, sourceName = '' }) {
    return mailEndpointRequest('mail_list_create', {}, {
      method: 'POST',
      body: { name, source_name: sourceName },
    });
  },

  importMailListChunk({ listId, chunkIndex, contacts }) {
    return mailEndpointRequest('mail_list_import_chunk', {}, {
      method: 'POST',
      body: {
        list_id: listId,
        chunk_index: chunkIndex,
        contacts,
      },
    });
  },

  finalizeMailList(listId, { totalCount = 0, invalidCount = 0, duplicateCount = 0 } = {}) {
    return mailEndpointRequest('mail_list_finalize', {}, {
      method: 'POST',
      body: {
        list_id: listId,
        source_total_count: totalCount,
        source_invalid_count: invalidCount,
        source_duplicate_count: duplicateCount,
      },
    });
  },

  getMailListContacts(listId, { page = 1, limit = 25, search = '', status = 'all' } = {}) {
    return mailEndpointRequest('mail_list_contacts', {
      list_id: listId,
      page,
      limit,
      search,
      status,
    });
  },

  archiveMailList(listId) {
    return mailEndpointRequest('mail_list_archive', {}, {
      method: 'POST',
      body: { list_id: listId },
    });
  },

  restoreMailList(listId) {
    return mailEndpointRequest('mail_list_restore', {}, {
      method: 'POST',
      body: { list_id: listId },
    });
  },

  getMailCampaigns({ page = 1, limit = 20, search = '', status = 'all' } = {}) {
    return mailEndpointRequest('mail_campaigns', { page, limit, search, status });
  },

  createMailCampaign({ listId, name, subject, body, dailyLimit, startAt, sendWindowStart, sendWindowEnd }) {
    return mailEndpointRequest('mail_campaign_create', {}, {
      method: 'POST',
      body: {
        list_id: listId,
        name,
        subject,
        body,
        daily_limit: dailyLimit,
        start_at: startAt,
        send_window_start: sendWindowStart,
        send_window_end: sendWindowEnd,
      },
    });
  },

  sendMailCampaignTest({ email, subject, body }) {
    return mailEndpointRequest('mail_campaign_test', {}, {
      method: 'POST',
      body: { email, subject, body },
    });
  },

  getMailCampaignDetail(campaignId, { page = 1, limit = 25, search = '', status = 'all' } = {}) {
    return mailEndpointRequest('mail_campaign_detail', {
      campaign_id: campaignId,
      page,
      limit,
      search,
      status,
    });
  },

  changeMailCampaignStatus(campaignId, action) {
    return mailEndpointRequest('mail_campaign_change_status', {}, {
      method: 'POST',
      body: { campaign_id: campaignId, action },
    });
  },

  getPaymentRecoveryDashboard() {
    return mailEndpointRequest('payment_recovery_dashboard');
  },

  getPaymentRecoveryRecipientHistory(recipientId) {
    return mailEndpointRequest('payment_recovery_recipient_history', {
      recipient_id: recipientId,
    });
  },

  savePaymentRecoverySettings(settings) {
    return mailEndpointRequest('payment_recovery_save_settings', {}, {
      method: 'POST',
      body: {
        enabled: Boolean(settings.enabled),
        wait_minutes: Number(settings.waitMinutes),
        auto_send: Boolean(settings.autoSend),
        per_minute_limit: Number(settings.perMinuteLimit),
        daily_limit: Number(settings.dailyLimit),
        subject: settings.subject,
        body: settings.body,
      },
    });
  },

  sendPaymentRecoveryTest({ email, subject, body }) {
    return mailEndpointRequest('payment_recovery_test_mail', {}, {
      method: 'POST',
      body: { email, subject, body },
    });
  },

  deletePaymentRecoveryRecipient(recipientId) {
    return mailEndpointRequest('payment_recovery_delete_recipient', {}, {
      method: 'POST',
      body: { recipient_id: recipientId },
    });
  },

  excludePaymentRecoveryRecipient(recipientId) {
    return mailEndpointRequest('payment_recovery_exclude_recipient', {}, {
      method: 'POST',
      body: { recipient_id: recipientId },
    });
  },

  resetPaymentRecoveryCounters() {
    return mailEndpointRequest('payment_recovery_reset_counters', {}, {
      method: 'POST',
      body: {},
    });
  },

  clearPaymentRecoveryHistory() {
    return mailEndpointRequest('payment_recovery_clear_history', {}, {
      method: 'POST',
      body: {},
    });
  },

  getAppSettings() {
    return appEndpointRequest('admin_app_settings_get');
  },

  saveAppSettings(platform, settings) {
    return appEndpointRequest('admin_app_settings_save', {}, {
      method: 'POST',
      body: { platform, settings },
    });
  },
};

export const tivigoApiUrls = {
  user: USER_ENDPOINT_URL,
  admin: ADMIN_ENDPOINT_URL,
  mail: MAIL_ENDPOINT_URL,
  app: APP_ENDPOINT_URL,
  player: PLAYER_API_URL,
  stream: STREAM_BASE_URL,
};
